/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { Alert, Box, Button, Container } from '@mui/material'
import Header from '../components/Headers/Header'
import DataTable from '../components/DataTable'
import orderBy from 'lodash/orderBy'
import CustomLoader from '../components/Loader/CustomLoader'
import { useQuery, useMutation, gql } from '@apollo/client'
import { reviews, deleteReview } from '../apollo'
import useGlobalStyles from '../utils/globalStyles'
import SearchBar from '../components/TableHeader/SearchBar'
import { customStyles } from '../utils/tableCustomStyles'
import TableHeader from '../components/TableHeader'
import { RowActionMenu } from '../components/UI/RowActionMenu'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import MuiRating from '@mui/material/Rating'
import Grid from '@mui/material/Grid'
import StarRounded from '@mui/icons-material/StarRounded'
import RateReviewRounded from '@mui/icons-material/RateReviewRounded'
import ThumbUpAltRounded from '@mui/icons-material/ThumbUpAltRounded'
import ThumbDownAltRounded from '@mui/icons-material/ThumbDownAltRounded'
import { StatTile, raisedButtonSx } from '../components/UI/Depth'
import AddRounded from '@mui/icons-material/AddRounded'
import AddRatingModal from '../components/Rating/AddRatingModal'

const REVIEWS = gql`
  ${reviews}
`
const DELETE_REVIEW = gql`
  ${deleteReview}
`

const Ratings = props => {
  const { t } = props
  const [searchQuery, setSearchQuery] = useState('')
  const [actionMessage, setActionMessage] = useState('')
  const [actionError, setActionError] = useState('')
  const [addRatingOpen, setAddRatingOpen] = useState(false)

  // Only a platform admin may record a review manually; a vendor must not be
  // able to write reviews for their own restaurant.
  const storedUser = localStorage.getItem('user-borku')
  const isSuperAdmin = storedUser
    ? JSON.parse(storedUser)?.userType === 'ADMIN'
    : false
  const onChangeSearch = e => setSearchQuery(e.target.value)
  const restaurantId = localStorage.getItem('restaurantId')

  useEffect(() => {
    if (!actionMessage && !actionError) return
    const timeoutId = setTimeout(() => {
      setActionMessage('')
      setActionError('')
    }, 3000)
    return () => clearTimeout(timeoutId)
  }, [actionMessage, actionError])

  const {
    data,
    error: errorQuery,
    loading: loadingQuery,
    refetch: refetchReviews
  } = useQuery(REVIEWS, {
    variables: { restaurant: restaurantId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only'
  })
  const [deleteReviewMutate, { loading: deletingReview }] = useMutation(
    DELETE_REVIEW,
    {
      onCompleted: async result => {
        setActionError('')
        setActionMessage(
          result?.deleteReview?.message || 'Review deleted successfully.'
        )
        await refetchReviews({ restaurant: restaurantId })
      },
      onError: err => {
        setActionMessage('')
        setActionError(err?.message || 'Failed to delete review.')
      }
    }
  )

  const handleDeleteReview = row => {
    const reviewId = row?._id
    if (!reviewId) return
    setActionMessage('')
    setActionError('')
    deleteReviewMutate({
      variables: { reviewId }
    })
  }

  const columns = [
    {
      name: t('Name'),
      sortable: true,
      selector: 'reviewerName',
      cell: row => <>{row?.reviewerName || '-'}</>
    },
    {
      name: t('Email'),
      sortable: true,
      selector: 'reviewerEmail',
      cell: row => <>{row?.reviewerEmail || '-'}</>
    },
    {
      name: t('Items'),
      cell: row => (
        <>
          {(row?.order?.items || []).map(({ title }) => {
            return title + '\t'
          })}
        </>
      )
    },
    {
      name: t('Review'),
      sortable: true,
      selector: 'description',
      cell: row => <>{row.description}</>
    },
    {
      name: t('Ratings'),
      sortable: true,
      selector: 'rating',
      cell: row => (
        <MuiRating
          value={Number(row.rating) || 0}
          precision={0.5}
          size="small"
          readOnly
        />
      )
    },
    {
      name: t('Action'),
      cell: row => (
        <RowActionMenu
          label={t('Action')}
          actions={[
            {
              label: t('Delete'),
              icon: DeleteOutlineRounded,
              tone: 'danger',
              disabled: deletingReview,
              confirm: {
                title: `${t('Delete')}?`,
                message: t('DeleteConfirm'),
                cancelLabel: t('Cancel')
              },
              onClick: () => handleDeleteReview(row)
            }
          ]}
        />
      )
    }
  ]
  const propExists = (obj, path) => {
    return path.split('.').reduce((obj, prop) => {
      return obj && obj[prop] ? obj[prop] : ''
    }, obj)
  }

  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (field && isNaN(propExists(row, field))) {
        return propExists(row, field).toLowerCase()
      }

      return row[field]
    }

    return orderBy(rows, handleField, direction)
  }

  const handleSort = (column, sortDirection) =>
    console.log(column.selector, sortDirection, column)

  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered =
    searchQuery.length < 3
      ? data && data.reviews
      : data &&
        data.reviews.filter(review => {
          const userName = review?.reviewerName?.toLowerCase?.() || ''
          const userEmail = review?.reviewerEmail?.toLowerCase?.() || ''
          return userName.search(regex) > -1 || userEmail.search(regex) > -1
        })
  const globalClasses = useGlobalStyles()
  const allReviews = (data && data.reviews) || []
  const average = allReviews.length
    ? allReviews.reduce(
        (sum, review) => sum + (Number(review.rating) || 0),
        0
      ) / allReviews.length
    : 0
  const ratingStats = [
    {
      label: t('AverageRating'),
      value: average ? average.toFixed(1) : '–',
      icon: StarRounded,
      tone: 'amber'
    },
    {
      label: t('TotalReviews'),
      value: allReviews.length,
      icon: RateReviewRounded,
      tone: 'blue'
    },
    {
      label: t('FiveStarReviews'),
      value: allReviews.filter(review => Number(review.rating) >= 5).length,
      icon: ThumbUpAltRounded,
      tone: 'green'
    },
    {
      label: t('LowRatings'),
      value: allReviews.filter(review => Number(review.rating) <= 2).length,
      icon: ThumbDownAltRounded,
      tone: 'rose'
    }
  ]
  return (
    <>
      {/* Sits in the page header like every other "Add" action, rather than
          being pulled up into the table header with a negative margin */}
      <Header
        action={
          isSuperAdmin ? (
            <Button
              startIcon={<AddRounded />}
              onClick={() => setAddRatingOpen(true)}
              sx={raisedButtonSx}>
              {t('AddRating')}
            </Button>
          ) : null
        }
      />
      {/* Page content */}
      <Container className={globalClasses.flex} fluid>
        {errorQuery && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {`${t('Error')} ${errorQuery.message}`}
          </Alert>
        )}
        {actionError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {actionError}
          </Alert>
        )}
        {actionMessage && (
          <Alert severity="success" sx={{ mb: 1 }}>
            {actionMessage}
          </Alert>
        )}
        {!loadingQuery ? (
          <Grid container spacing={2.5} mb={3}>
            {ratingStats.map(stat => (
              <Grid item xs={6} lg={3} key={stat.label}>
                <StatTile {...stat} />
              </Grid>
            ))}
          </Grid>
        ) : null}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <DataTable
            title={<TableHeader title={t('Ratings')} />}
            subHeader={true}
            subHeaderComponent={
              <SearchBar value={searchQuery} onChange={onChangeSearch} />
            }
            columns={columns}
            data={filtered}
            pagination
            progressPending={loadingQuery}
            progressComponent={<CustomLoader />}
            onSort={handleSort}
            sortFunction={customSort}
            defaultSortField="reviewerName"
            customStyles={customStyles}
            paginationIconLastPage=""
            paginationIconFirstPage=""
          />
        )}
        <AddRatingModal
          open={addRatingOpen}
          onClose={() => setAddRatingOpen(false)}
          restaurantId={restaurantId}
          onCreated={() => {
            setActionMessage(t('RatingAdded'))
            refetchReviews()
          }}
        />
      </Container>
    </>
  )
}

export default withTranslation()(Ratings)
